import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { Phone, Mail, MapPin, MessageCircle, Clock, ArrowRight } from "lucide-react";
import { API } from "@/config";
import { toast } from "sonner";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Contact", item: `${SITE}/contact` },
  ],
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in name, email and message."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch {
      toast.error("Message not sent — please call or text us directly at 689-345-RENT.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageSEO
        title="Contact Enchanted Gear Rentals | Call, Text or Email"
        description="Contact Enchanted Gear Rentals by phone, text or email. We serve the greater Orlando area including Walt Disney World, Universal Orlando and vacation homes."
        canonical={`${SITE}/contact`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>
        <section className="pt-28 pb-12 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-2xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Contact</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-4">Contact Us</h1>
            <p className="text-[#B9C8D5]">We are a family-owned business. Real people answer our phone and texts — no bots, no automated hold music.</p>
          </div>
        </section>

        <div className="container max-w-4xl py-12 pb-20">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Contact info */}
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Get in touch</h2>
                <div className="space-y-4">
                  <a href="tel:+16893457368" className="flex items-start gap-4 group" data-testid="contact-phone">
                    <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#FFD166]" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B9C8D5] mb-0.5">Call or text</div>
                      <div className="text-[#F6F1E4] font-semibold group-hover:text-[#FFD166] transition-colors">689-345-RENT (7368)</div>
                      <div className="text-xs text-[#B9C8D5] mt-0.5">Customer service — calls and texts answered</div>
                    </div>
                  </a>
                  <a href="sms:+16893457368" className="flex items-start gap-4 group" data-testid="contact-sms">
                    <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-[#FFD166]" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B9C8D5] mb-0.5">Text us</div>
                      <div className="text-[#F6F1E4] font-semibold group-hover:text-[#FFD166] transition-colors">689-345-RENT (7368)</div>
                    </div>
                  </a>
                  <a href="mailto:info@enchantedgearrentals.com" className="flex items-start gap-4 group" data-testid="contact-email">
                    <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#FFD166]" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B9C8D5] mb-0.5">Email</div>
                      <div className="text-[#F6F1E4] font-semibold group-hover:text-[#FFD166] transition-colors">info@enchantedgearrentals.com</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#FFD166]" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B9C8D5] mb-0.5">Service area</div>
                      <div className="text-[#F6F1E4] text-sm">Greater Orlando, FL — Walt Disney World, Universal Orlando, Kissimmee, Celebration, Reunion, Bonnet Creek and surrounding vacation-rental communities.</div>
                      <div className="text-xs text-[#B9C8D5] mt-1">We are a delivery-only service. We do not have a public storefront.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#FFD166]/10 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-[#FFD166]" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-[#B9C8D5] mb-0.5">Hours</div>
                      <div className="text-[#F6F1E4] text-sm">Deliveries 7 days a week, 8 am – 10 pm<br />Phone &amp; text available during delivery hours</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-4">
                <p className="text-xs text-[#B9C8D5]">
                  Note: 833-805-GEAR (4327) is our automated order-updates number only. For customer service — including reservations, questions and changes — please use 689-345-RENT (7368) or email.
                </p>
              </div>

              <Link to="/#trip-setup">
                <button className="btn-primary w-full" data-testid="contact-book-cta">Reserve a Stroller <ArrowRight className="inline w-4 h-4 ml-1" /></button>
              </Link>
            </div>

            {/* Contact form */}
            <div className="rounded-2xl bg-[#0A1825] border border-[#FFD166]/15 p-7">
              <h2 className="font-serif text-xl text-[#F6F1E4] mb-5">Send us a message</h2>
              {sent ? (
                <div className="text-center py-8">
                  <div className="text-[#FFD166] text-4xl mb-3">✓</div>
                  <div className="font-serif text-xl text-[#F6F1E4] mb-2">Message received</div>
                  <p className="text-[#B9C8D5] text-sm">We will get back to you shortly. For urgent inquiries, call or text us at 689-345-RENT.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#B9C8D5] mb-1" htmlFor="contact-name">Name</label>
                    <input id="contact-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#060f18] border border-[#FFD166]/20 rounded-lg px-4 py-2.5 text-[#F6F1E4] focus:outline-none focus:border-[#FFD166]/60 text-sm" placeholder="Your name" data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#B9C8D5] mb-1" htmlFor="contact-email">Email</label>
                    <input id="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#060f18] border border-[#FFD166]/20 rounded-lg px-4 py-2.5 text-[#F6F1E4] focus:outline-none focus:border-[#FFD166]/60 text-sm" placeholder="your@email.com" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#B9C8D5] mb-1" htmlFor="contact-phone">Phone (optional)</label>
                    <input id="contact-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#060f18] border border-[#FFD166]/20 rounded-lg px-4 py-2.5 text-[#F6F1E4] focus:outline-none focus:border-[#FFD166]/60 text-sm" placeholder="(407) 000-0000" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-[#B9C8D5] mb-1" htmlFor="contact-message">Message</label>
                    <textarea id="contact-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#060f18] border border-[#FFD166]/20 rounded-lg px-4 py-2.5 text-[#F6F1E4] focus:outline-none focus:border-[#FFD166]/60 text-sm resize-none" placeholder="How can we help?" data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="contact-submit">
                    {submitting ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Service area links */}
          <div className="mt-14">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Delivery information</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: "Walt Disney World", desc: "In-person lobby delivery to all Disney resort hotels.", link: "/delivery/walt-disney-world-resorts" },
                { title: "Universal Orlando", desc: "Delivery to Universal resort hotels and nearby properties.", link: "/delivery/universal-orlando" },
                { title: "Vacation Homes", desc: "Airbnb, VRBO and vacation-rental delivery across the Orlando area.", link: "/delivery/vacation-homes" },
              ].map((a) => (
                <Link key={a.title} to={a.link} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-4 transition-all">
                  <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-1">{a.title}</div>
                  <div className="text-sm text-[#B9C8D5]">{a.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
